import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { canIssuePhysicalCommands } from '../../auth/cognito';
import ParcelConfirmDialog from '../../components/ParcelConfirmDialog';
import {
  useParcelValveCommand,
  type ParcelCommandOutcome,
} from '../../hooks/useParcelValveCommand';
import { parcelControlAvailability, parcelsFor, parcelValveState, parcelMayBePowerSaving } from '../../lib/parcelOperational';
import { commandStatusKind, isBusyStatus } from '../../lib/valveCommandSafety';
import SoverdiSiteSelector from '../../soverdi-clone/components/SoverdiSiteSelector';
import type { CgmValveControl } from './valveControlPresentation';
import { useAgriSafeSoverdiDashboard } from '../../soverdi-clone/hooks/useAgriSafeSoverdiDashboard';
import { orderedSiteNames } from '../../soverdi-clone/adapters/dashboardContext';
import type { SoverdiSectorView } from '../../soverdi-clone/adapters/agriSafeDashboardAdapter';
import CgmHomeParcelsR1 from './CgmHomeParcelsR1';
import { FieldBindingsProvider } from '../../hooks/useFieldBindings';

const CgmDashboardContent: React.FC = () => {
  const { t } = useTranslation('agrisafeR1');
  const { user } = useAuth();
  const dashboard = useAgriSafeSoverdiDashboard();
  const { siteModels, parcelBindings, nodes, refreshNodes } = dashboard;
  const [selectedSiteName, setSelectedSiteName] = useState('');
  const [selectedBindingBySector, setSelectedBindingBySector] = useState<Record<string, string>>({});

  const siteNames = useMemo(() => orderedSiteNames(siteModels), [siteModels]);
  const effectiveSiteName = selectedSiteName || siteNames[0] || '';

  const command = useParcelValveCommand({
    role: user?.role,
    nodes,
    refreshNodes,
  });

  const outcomeLabel = useCallback((outcome: ParcelCommandOutcome): string | null => {
    switch (outcome) {
      case 'unassigned': return t('sectorDetail.notAssigned');
      case 'semantic_binding_required': return t('dashboard.irrigationMappingToConfirm');
      case 'commissioning_incomplete': return t('parcel.lockReason.COMMISSIONING_NOT_COMPLETE');
      case 'actuation_not_authorized': return t('parcel.lockReason.ACTUATION_NOT_AUTHORIZED');
      case 'wiring_not_confirmed': return t('pumping.diagnostic.channelPending');
      case 'conflict': return t('parcel.status.conflict');
      case 'forbidden': return t('parcel.status.forbidden');
      case 'uncertain': return t('parcel.status.uncertain');
      default: return null;
    }
  }, [t]);

  const resolveValveControl = useCallback((sector: SoverdiSectorView): CgmValveControl | null => {
    const sectorCandidates = parcelsFor(parcelBindings, sector.siteName, sector.sectorCode);
    const candidates = 'bindingId' in sector
      ? sectorCandidates.filter((candidate) => candidate.binding_id === sector.bindingId)
      : sectorCandidates;
    if (candidates.length === 0) return null;

    const bindingOptions = candidates.map((candidate) => ({
      bindingId: candidate.binding_id,
      label: dashboard.parcelDisplayName?.(candidate) ?? candidate.parcel,
    }));
    const selectedBindingId = candidates.length === 1
      ? candidates[0].binding_id
      : selectedBindingBySector[sector.id] ?? null;
    const binding = selectedBindingId
      ? candidates.find((candidate) => candidate.binding_id === selectedBindingId)
      : undefined;

    const onSelectBinding = (bindingId: string) => {
      setSelectedBindingBySector((previous) => {
        const next = { ...previous };
        if (bindingId && candidates.some((candidate) => candidate.binding_id === bindingId)) {
          next[sector.id] = bindingId;
        } else {
          delete next[sector.id];
        }
        return next;
      });
    };

    if (!binding) {
      return {
        canOpen: false,
        canClose: false,
        valveState: 'unknown',
        commandStatus: undefined,
        commandOutcome: null,
        statusText: null,
        availabilityText: t('dashboard.irrigationMappingToConfirm'),
        bindingOptions,
        selectedBindingId: null,
        bindingSelectionLabel: t('parcel.confirm.parcel'),
        onSelectBinding,
      };
    }

    const node = nodes.find((item) => item.id === binding.controller_node_id);
    const state = parcelValveState(node, binding.channel_id, Date.now());
    const rawStatus = command.statusFor(binding.binding_id);
    const outcome = command.outcomeFor(binding.binding_id);
    const busy = isBusyStatus(rawStatus);
    const roleAuthorized = canIssuePhysicalCommands(user?.role);
    const authorized = binding.command_authorized && roleAuthorized && outcome === null;
    const { canOpen, canClose } = parcelControlAvailability(state, authorized, busy);

    const statusKind = commandStatusKind(rawStatus);
    const statusText = outcomeLabel(outcome)
      ?? (busy
        ? rawStatus === 'UNCERTAIN'
          ? t('parcel.status.uncertain')
          : t('parcel.status.busy')
        : statusKind === 'confirmed'
          ? t('sectorDetail.commandState.confirmed')
          : statusKind === 'failed'
            ? t('sectorDetail.commandState.failed')
            : statusKind === 'timeout'
              ? t('parcel.status.timeout')
              : statusKind === 'uncertain'
                ? t('parcel.status.uncertain')
                : null);

    let availabilityText: string | null = null;
    if (!binding.command_authorized) {
      switch (binding.lock_reason) {
        case 'BINDING_NOT_CONFIRMED':
          availabilityText = t('parcel.lockReason.BINDING_NOT_CONFIRMED');
          break;
        case 'COMMISSIONING_NOT_COMPLETE':
          availabilityText = t('parcel.lockReason.COMMISSIONING_NOT_COMPLETE');
          break;
        case 'ACTUATION_NOT_AUTHORIZED':
          availabilityText = t('parcel.lockReason.ACTUATION_NOT_AUTHORIZED');
          break;
        case 'PHYSICAL_INSTALLATION_NOT_CONFIRMED':
        case 'WIRING_NOT_CONFIRMED':
        case 'LOCAL_TEST_NOT_OK':
        case 'REMOTE_TEST_NOT_OK':
          availabilityText = t('pumping.diagnostic.channelPending');
          break;
        default:
          availabilityText = t('parcel.locked');
      }
    } else if (!roleAuthorized) {
      availabilityText = t('sectorDetail.block.notAuthorized');
    } else if (busy) {
      availabilityText = statusText ?? t('sectorDetail.block.commandBusy');
    }

    return {
      canOpen,
      canClose,
      valveState: state,
      powerSavingPossible: !dashboard.loading && !dashboard.loadError && !dashboard.bindingsUnavailable
        && !statusText && outcome === null && parcelMayBePowerSaving(binding, node, state),
      commandStatus: rawStatus,
      commandOutcome: outcome,
      statusText,
      availabilityText,
      bindingOptions: candidates.length > 1 ? bindingOptions : undefined,
      selectedBindingId,
      bindingSelectionLabel: candidates.length > 1 ? t('parcel.confirm.parcel') : null,
      onSelectBinding: candidates.length > 1 ? onSelectBinding : undefined,
      onOpen: () => command.requestCommand(binding, 'open'),
      onClose: () => command.requestCommand(binding, 'close'),
    };
  }, [dashboard.parcelDisplayName, dashboard.loading, dashboard.loadError, dashboard.bindingsUnavailable, parcelBindings, nodes, command, user?.role, outcomeLabel, selectedBindingBySector, t]);

  return (
    <div className="space-y-5" data-testid="cgm-dashboard-live-r1">
      <SoverdiSiteSelector
        sites={siteNames}
        selected={effectiveSiteName}
        onSelect={setSelectedSiteName}
      />

      <CgmHomeParcelsR1
        siteName={effectiveSiteName}
        dashboard={dashboard}
        resolveValveControl={resolveValveControl}
      />

      {command.pending && (
        <ParcelConfirmDialog
          pending={command.pending}
          onCancel={command.cancelConfirmation}
          onConfirm={() => { void command.confirmCommand(); }}
        />
      )}
    </div>
  );
};

const CgmDashboardLiveR1: React.FC = () => {
  const { user } = useAuth();
  return <FieldBindingsProvider key={JSON.stringify([user?.org_id, user?.id])}>
    <CgmDashboardContent />
  </FieldBindingsProvider>;
};
export default CgmDashboardLiveR1;
